/*
  Filter + sort + pagination as ONE state, with one reducer, because separately they are
  the bug §6.5 records.

  "Changing the filter resets pagination to page 0" is already true of the old
  tableReducer (helpers/tables.tsx:122-128) and false of every view that ships, because
  all four views hold their filter in a useState of their own that the reducer never sees:
  GameView.tsx:34,95, SetView.tsx:21,87, CardView.tsx:20,44 and CardGradeView.tsx:52,80.
  The rule was written down and then bypassed four times.

  So the filter is not something a view holds beside the table state — it is part of it,
  and the only way to change it is an action that also resets the page. A consumer cannot
  forget the rule because there is no API through which to forget it.

  The pipeline is filter -> sort -> paginate, in that order, in selectTableRows. Order is
  not a preference: filtering after paginating would filter one page of rows and drop the
  rest, and sorting after paginating would sort within a page. It is composed here, once.

  Pure on purpose — component tests do not run in this repo (RTL 13 against React 17,
  spec §2), so this is the unit the plan asks for and useTableState.ts is the thin React
  wrapper over it.
*/

import { SortDirection } from "lib";

import { filterRows } from "./filtering";
import { sortRowsByColumn } from "./columns";
import { clampPage, DEFAULT_ROWS_PER_PAGE, pageRows } from "./pagination";
import { DataTableColumn, TableSortState } from "./types";

export interface TableState {
  filter: string;
  sort: TableSortState;
  page: number;
  rowsPerPage: number;
}

export type TableStateAction =
  | { type: "filter"; value: string }
  | { type: "sort"; value: TableSortState }
  | { type: "page"; value: number }
  | { type: "rowsPerPage"; value: number };

export interface TableStateInit {
  orderBy?: string;
  order?: SortDirection;
  rowsPerPage?: number;
  filter?: string;
}

export const createTableState = (init: TableStateInit = {}): TableState => ({
  filter: init.filter ?? "",
  sort: { orderBy: init.orderBy ?? "", order: init.order ?? "asc" },
  page: 0,
  rowsPerPage: init.rowsPerPage ?? DEFAULT_ROWS_PER_PAGE,
});

/*
  Which actions reset the page, and why:

    filter        yes — §6.5, the whole point of this module.
    rowsPerPage   yes — page 3 of 10-row pages is not page 3 of 50-row pages. Matches the
                  old reducer's "limit" case and MUI's TablePagination.
    sort          no  — the old reducer's "sort" case leaves the page alone (§6.6 says
                  carry the structure over), and re-sorting is how a visitor looks for the
                  top of a column, which page 0 shows them anyway once they get there.
    page          it IS the page.
*/
export const tableStateReducer = (
  state: TableState,
  action: TableStateAction
): TableState => {
  switch (action.type) {
    case "filter":
      return { ...state, filter: action.value, page: 0 };
    case "sort":
      return { ...state, sort: action.value };
    case "page":
      return { ...state, page: action.value < 0 ? 0 : action.value };
    case "rowsPerPage":
      return { ...state, rowsPerPage: action.value, page: 0 };
    default:
      return state;
  }
};

export interface TableRowsResult<TRow> {
  /* The one page the table renders. */
  rows: TRow[];
  /* Everything that survived the filter, in sort order — what pagination counts. */
  filteredRows: TRow[];
  /* filteredRows.length, i.e. the pagination's `count`. */
  filteredCount: number;
  /*
    The page after clamping. A filter that shortens the results below the current page
    would otherwise paint a blank table; feed this back to the pagination component so the
    control and the rows agree.
  */
  page: number;
}

export const selectTableRows = <TRow>(
  rows: readonly TRow[],
  columns: readonly DataTableColumn<TRow>[],
  state: TableState
): TableRowsResult<TRow> => {
  const filtered = filterRows(rows, columns, state.filter);
  const sorted = sortRowsByColumn(filtered, columns, state.sort);
  const page = clampPage(state.page, sorted.length, state.rowsPerPage);

  return {
    rows: pageRows(sorted, page, state.rowsPerPage),
    filteredRows: sorted,
    filteredCount: sorted.length,
    page,
  };
};

/*
  Which of the four things a table shows (§6.2: "no bare blank table").

  Loading beats error: a fetch in flight is a more current fact than the error from the
  attempt before it, and RTK Query keeps `error` set across a refetch. When the refetch
  settles, isLoading goes false and a still-set error surfaces.
*/
export type TableStatus = "loading" | "error" | "empty" | "rows";

export const resolveTableStatus = (
  isLoading: boolean,
  error: unknown,
  rowCount: number
): TableStatus => {
  if (isLoading) {
    return "loading";
  }
  if (error !== null && error !== undefined && error !== false) {
    return "error";
  }
  return rowCount === 0 ? "empty" : "rows";
};
